import { Injectable } from '@angular/core';
import { ValApiResultat, ValBaseService } from '../../../../val-ammon-services/val-services-communs-lib/src/public-api';
import { ValAppSettingsService } from '../../../../val-ammon-services/val-app-settings-lib/src/public-api';
import { RetournerVueVenteResultat } from './RetournerVueVenteResultat';
import { RetournerVueVenteCycleVieResultat } from './RetournerVueVenteCycleVieResultat';
import { RetournerVueVenteEncaissementsResultat } from './RetournerVueVenteEncaissementsResultat';
import { RetournerVueVenteDocumentResultat } from './RetournerVueVenteDocumentResultat';
import { RetournerDocumentVenteResultat } from './RetournerDocumentVenteResultat';
import { FiltreAccueilPeriode } from './FiltreAccueilPeriode';
import { RetournerStatistiquesFacturesTransmisesResultat } from './RetournerStatistiquesFacturesTransmisesResultat';
import { RetournerStatistiquesSuiviRegularisationsResultat } from './RetournerStatistiquesSuiviRegularisationsResultat';
import { RetournerStatistiquesRepartitionParStatutResultat } from './RetournerStatistiquesRepartitionParStatutResultat';
import { RetournerStatistiquesPodiumMotifsRetoursResultat } from './RetournerStatistiquesPodiumMotifsRetoursResultat';
import { StatutManuelTransitionResultat } from './StatutManuelTransitionResultat';
import { ModifierStatutManuelResultat } from './ModifierStatutManuelResultat';
import { MajStatutPieceRequest } from './MajStatutPieceRequest';

// Service eFact vente
@Injectable({ providedIn: 'root' })
export class EFactVenteService extends ValBaseService {

  constructor(private appSettingsService: ValAppSettingsService) {
    super();
  }

  protected override get BaseUrl(): string {
    return `${this.appSettingsService.Configuration.UrlApi.EFact}/v1.0/sales/`;
  }

  // Retourne la vue d'une vente
  public async RetournerVueVente(id: string | number): Promise<ValApiResultat<RetournerVueVenteResultat>> {
    const url = `${id}/view`;
    return this.ExecuterRequeteGet<RetournerVueVenteResultat>(url);
  }

  // Retourne la vue de l'historique du cycle de vie d'une vente
  public async RetournerVueHistoriqueCycleVieVente(id: string | number): Promise<ValApiResultat<RetournerVueVenteCycleVieResultat[]>> {
    const url = `${id}/view/lifecycle-history`;
    return this.ExecuterRequeteGet<RetournerVueVenteCycleVieResultat[]>(url);
  }

  // Retourne la vue des encaissements d'une vente
  public async RetournerVueEncaissementsVente(id: string | number): Promise<ValApiResultat<RetournerVueVenteEncaissementsResultat[]>> {
    const url = `${id}/view/payments`;
    return this.ExecuterRequeteGet<RetournerVueVenteEncaissementsResultat[]>(url);
  }

  // Retourne la vue des documents liés à une vente
  public async RetournerVueDocumentsLiesVente(id: string | number): Promise<ValApiResultat<RetournerVueVenteDocumentResultat[]>> {
    const url = `${id}/view/documents`;
    return this.ExecuterRequeteGet<RetournerVueVenteDocumentResultat[]>(url);
  }

  // Retourne le contenu d'un document lié à une vente
  public async RetournerDocumentVente(id: string | number, idDocument: string): Promise<ValApiResultat<RetournerDocumentVenteResultat>> {
    const url = `${id}/documents/${idDocument}`;
    return this.ExecuterRequeteGet<RetournerDocumentVenteResultat>(url);
  }

  // Retourne tous les documents liés à une vente dans un zip
  public async RetournerDocumentsVenteZip(id: string | number): Promise<ValApiResultat<RetournerDocumentVenteResultat>> {
    const url = `${id}/documents`;
    return this.ExecuterRequeteGet<RetournerDocumentVenteResultat>(url);
  }

  // Génère la facture électronique d'une vente (PDF/A-3 si lisible disponible, sinon XML FacturX)
  public async GenererEFacture(id: string | number): Promise<ValApiResultat<RetournerDocumentVenteResultat>> {
    const url = `${id}/generate/e-invoice`;
    return this.ExecuterRequetePost<RetournerDocumentVenteResultat>(url, {});
  }

  // Retourne les statistiques des factures transmises
  public async RetournerStatistiquesFacturesTransmises(filtre: FiltreAccueilPeriode): Promise<ValApiResultat<RetournerStatistiquesFacturesTransmisesResultat>> {
    const url = `statistics/transmitted-invoices?period=${filtre}`;
    return this.ExecuterRequeteGet<RetournerStatistiquesFacturesTransmisesResultat>(url);
  }

  // Retourne les statistiques de suivi des régularisations
  public async RetournerStatistiquesSuiviRegularisations(filtre: FiltreAccueilPeriode): Promise<ValApiResultat<RetournerStatistiquesSuiviRegularisationsResultat>> {
    const url = `statistics/regularization-tracking?period=${filtre}`;
    return this.ExecuterRequeteGet<RetournerStatistiquesSuiviRegularisationsResultat>(url);
  }

  // Retourne les statistiques de répartition des factures de vente par statut
  public async RetournerStatistiquesRepartitionParStatut(filtre: FiltreAccueilPeriode): Promise<ValApiResultat<RetournerStatistiquesRepartitionParStatutResultat>> {
    const url = `statistics/status-breakdown?period=${filtre}`;
    return this.ExecuterRequeteGet<RetournerStatistiquesRepartitionParStatutResultat>(url);
  }

  // Retourne les statistiques du top 3 des motifs de retours dans le cycle de vie
  public async RetournerStatistiquesPodiumMotifsRetours(filtre: FiltreAccueilPeriode): Promise<ValApiResultat<RetournerStatistiquesPodiumMotifsRetoursResultat>> {
    const url = `statistics/top3-return-reasons?period=${filtre}`;
    return this.ExecuterRequeteGet<RetournerStatistiquesPodiumMotifsRetoursResultat>(url);
  }

  // Retourne les statuts pouvant être positionnés sur la pièce de vente, en fonction du statut actuel de la pièce
  public async RecupererTransitionsModificationStatut(id: string | number): Promise<ValApiResultat<StatutManuelTransitionResultat[]>> {
    const url = `${id}/status/transitions`;
    return this.ExecuterRequeteGet<StatutManuelTransitionResultat[]>(url);
  }

  // Modifie manuellement le statut d'une vente
  public async ModifierStatutManuel(id: string | number, nouveauStatutId: string): Promise<ValApiResultat<ModifierStatutManuelResultat>> {
    const url = `${id}/status`;
    return this.ExecuterRequetePatch<ModifierStatutManuelResultat>(url, { nouveauStatutId });
  }

  // Synchronise le statut actuel des pièces de vente avec la source externe
  public async SynchroniserStatutActuelPieces(request: MajStatutPieceRequest): Promise<void> {
    await this.ExecuterRequetePatch<void>("status/propagate", request);
  }
}
